// web-front/src/forecast-app/shared/components/dropdowns/project-client-contact-dropdown/ProjectClientContactDropdown.tsx
import React, {useEffect, useRef, useState} from 'react';
import {graphql} from 'react-relay';
import {fetchQuery} from 'relay-runtime';
import {Dropdown} from '@/web-components';
import {Label, FlexColumn} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import environment from '../../../../../RelayEnvironment';

const ProjectClientContactDropdownQuery = graphql`
	query ProjectClientContactDropdownQuery($clientId: String, $first: Int) {
		viewer {
			company {
				client(id: $clientId) {
					clientContacts(first: $first) {
						edges {
							node {
								id
								firstName
								lastName
							}
						}
					}
				}
			}
		}
	}
`;

// The full contact list for a client is loaded in a single request — the same approach the Client
// picker uses (it loads every client up-front), so the two dropdowns behave identically: one fetch,
// then client-side type-to-filter. This is the project-side Contact picker; it is built on the
// `@/web-components` Dropdown so it matches the Client field on the same screen (IGNITE-490). The
// edit-client modal keeps its own `ClientPrimaryContactDropdown` (design-system ComboBox).
const MAX_CONTACTS = 1000;

interface Contact {
	id: string;
	name: string;
}

interface ClientContactNode {
	id: string;
	firstName: string | null;
	lastName: string | null;
}

interface ClientContactsQueryResponse {
	viewer?: {
		company?: {
			client?: {
				clientContacts?: {
					edges: Array<{node: ClientContactNode}>;
				};
			};
		};
	};
}

interface ProjectClientContactDropdownProps {
	clientId: string;
	value: Contact | null;
	onChange: (contact: Contact | null) => void;
	label?: string;
	// When the dropdown is embedded in a layout that already renders its own label
	// (e.g. the project settings InputLine rows), hide the built-in label.
	hideLabel?: boolean;
	placeholder?: string;
	// Forwarded to the underlying Dropdown as data-cy so the picker can be targeted in tests.
	dataCy?: string;
}

export const ProjectClientContactDropdown = ({
	clientId,
	value,
	onChange,
	label,
	hideLabel,
	placeholder,
	dataCy,
}: ProjectClientContactDropdownProps) => {
	const {formatMessage} = useIntl();
	const [contacts, setContacts] = useState<Contact[]>(value ? [value] : []);
	// Tracks whether the request has resolved, so we don't flash the empty state while loading.
	const [loaded, setLoaded] = useState(false);
	// Read the current value inside the fetch without re-fetching every time the selection changes.
	const valueRef = useRef(value);
	valueRef.current = value;

	useEffect(() => {
		let cancelled = false;
		setLoaded(false);
		fetchQuery(environment.getInstance(), ProjectClientContactDropdownQuery, {
			clientId,
			first: MAX_CONTACTS,
		})
			.toPromise()
			.then(
				(data: ClientContactsQueryResponse | undefined) => {
					if (cancelled) return;
					const edges = data?.viewer?.company?.client?.clientContacts?.edges ?? [];
					const nodes: Contact[] = edges.map(e => ({
						id: e.node.id,
						name: [e.node.firstName, e.node.lastName].filter(Boolean).join(' '),
					}));
					// Keep the currently-selected contact visible even if it isn't in the returned list.
					const ids = new Set(nodes.map(n => n.id));
					const selected = valueRef.current;
					const preserved = selected && !ids.has(selected.id) ? [selected] : [];
					setContacts([...preserved, ...nodes]);
					setLoaded(true);
				},
				() => {
					if (!cancelled) setLoaded(true);
				}
			);
		return () => {
			cancelled = true;
		};
	}, [clientId]);

	const dropdownLabel = label ?? formatMessage({id: 'clients.client-contact', defaultMessage: 'Primary Contact'});
	const selectPlaceholder = placeholder ?? formatMessage({id: 'project_settings.select_contact'});
	// Client selected but it has no contacts: surface the empty state in the collapsed control, the
	// same way the Client picker shows "No clients" when a company has none.
	const isEmpty = loaded && contacts.length === 0;
	const name = isEmpty ? formatMessage({id: 'clients.client-contacts-empty-state'}) : selectPlaceholder;

	const handleSelect = (selected: string[]) => {
		const id = selected[0];
		const found = contacts.find(c => c.id === id) ?? null;
		onChange(found);
	};

	const dropdown = (
		<Dropdown
			isClearable
			selectedItems={value ? [value.id] : []}
			onSelect={handleSelect}
			onRemove={() => onChange(null)}
			name={name}
			cy={dataCy}
		>
			{contacts.map(contact => (
				<Dropdown.SingleText key={contact.id} value={contact.id} searchString={contact.name}>
					{contact.name}
				</Dropdown.SingleText>
			))}
		</Dropdown>
	);

	if (hideLabel) {
		return dropdown;
	}

	return (
		<FlexColumn gap={'s'}>
			<Label>{dropdownLabel}</Label>
			{dropdown}
		</FlexColumn>
	);
};
